import { useCallback, useEffect, useState } from "react";
import { clearCredentials, deviceConfig, getStoredToken, storeCredentials } from "../lib/deviceApi";
import { TvHome } from "./TvHome";
import { TvPairing } from "./TvPairing";
import { TvPlayer } from "./TvPlayer";
import "./tv.css";

// Standalone TV entry point (/tv). NEVER holds a Supabase session — only the scoped
// wwd_ device token. State machine: loading -> pairing <-> player <-> home.
export default function TvApp() {
  const [phase, setPhase] = useState("loading"); // loading | pairing | player | home
  const [notice, setNotice] = useState(null);    // shown on pairing after expiry/revoke

  useEffect(() => {
    (async () => {
      const token = getStoredToken();
      if (!token) { setPhase("pairing"); return; }
      const r = await deviceConfig(token);
      if (r.ok) { setPhase("player"); return; }
      if (r.status === 401 || r.status === 403) {
        clearCredentials();
        setNotice(r.status === 403
          ? "This TV was unlinked from your account. Link it again to continue."
          : "This TV's session expired. Link it again to continue.");
        setPhase("pairing");
        return;
      }
      // transient/network — let the player mount and handle reconnect/backoff.
      setPhase("player");
    })();
  }, []);

  const onLinked = useCallback((token, deviceId) => {
    storeCredentials(token, deviceId);
    setNotice(null);
    setPhase("player");
  }, []);

  // Long-lived TV clients must not stay on an obsolete build after a deployment. Poll the build
  // manifest (no-store so it is never cached) and reload when the hashed main bundle changes.
  // No service worker needed; harmless where the manifest is absent (e.g. the CRA dev server).
  useEffect(() => {
    let currentHash = null;
    const check = async () => {
      try {
        const res = await fetch("/asset-manifest.json", { cache: "no-store" });
        if (!res.ok) return;
        const manifest = await res.json();
        const hash = manifest?.files?.["main.js"] || null;
        if (!hash) return;
        if (currentHash === null) { currentHash = hash; return; }
        if (hash !== currentHash) window.location.reload();
      } catch (_e) { /* offline / transient — try again next tick */ }
    };
    check();
    const id = setInterval(check, 10 * 60 * 1000); // every 10 minutes
    return () => clearInterval(id);
  }, []);

  const onUnlinked = useCallback((reason) => {
    clearCredentials();
    setNotice(reason || null);
    setPhase("pairing");
  }, []);

  // Exit (X) from the player: leave the slideshow but PRESERVE the wwd_ token and show
  // the branded paired-TV home instead of the public logged-out landing.
  const onExitToHome = useCallback(() => { setPhase("home"); }, []);
  const onResume = useCallback(() => { setPhase("player"); }, []);

  if (phase === "loading") {
    return (
      <div className="tv-root tv-brandbg tv-center" data-testid="tv-loading-root">
        <div className="tv-spinner-text">World Wide Wallpaper</div>
      </div>
    );
  }
  if (phase === "pairing") return <TvPairing notice={notice} onLinked={onLinked} />;
  if (phase === "home") return <TvHome onResume={onResume} onUnlinked={onUnlinked} />;
  return <TvPlayer onUnlinked={onUnlinked} onExit={onExitToHome} />;
}
